import Panel from "../panel/Panel";
import React, {useEffect, useMemo, useState} from "react";
import {getShopClients} from "../../api/getShopClients";
import useUserContext from "../../hooks/useUserContext";
import {getShopClient} from "../../api/getShopClient";
import {
    formatDateForAmPmDisplay,
    formatDateWithAMPM,
    getFriendlyDate,
    getFriendlyTimeDifferenceV2, millisecondsToHuman
} from "../../utils/friendlyDate";
import "./client-panel.css";
import Avatar from "../../assets/icons/Avatar";
import {CheckCircleFilled} from "@ant-design/icons";
import {formatPhoneNumber} from "../../utils/friendlyPhone";
import millify from "millify";
import {Tooltip} from "antd";
import {ConversationRow} from "../../types/ConversationProps";
import Loader from "../loader/Loader";

const ClientPanel = ({ clientId, onClose, onConversationClick }: { clientId?: number; onClose: () => void, onConversationClick: (conversation: ConversationRow) => void }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [client, setClient] = useState<any | null>(null);
    const { selectedDomain } = useUserContext();
    const subscribed = client?.connection?.smsConsent || client?.connection?.emailConsent;

    const messagesSent = client?.messages?.length;
    const messagesClicked = useMemo(() => client?.messages?.filter((message: any) => !!message.clicks)?.length, [client?.messages]);
    const abandonedMessagesSent = useMemo(() => client?.messages?.filter((message: any) => message.checkoutId)?.length, [client?.messages]);
    const abandonedMessagesClicked = useMemo(() => client?.messages?.filter((message: any) => message.checkoutId && !!message.clicks)?.length, [client?.messages]);
    const eventMessagesSent = useMemo(() => client?.messages?.filter((message: any) => message.eventId)?.length, [client?.messages]);
    const eventMessagesClicked = useMemo(() => client?.messages?.filter((message: any) => message.eventId && !!message.clicks)?.length, [client?.messages]);
    const campaignMessagesSent = useMemo(() => client?.messages?.filter((message: any) => message.campaignId)?.length, [client?.messages]);
    const campaignMessagesClicked = useMemo(() => client?.messages?.filter((message: any) => message.campaignId && !!message.clicks)?.length, [client?.messages]);

    const getClient = async () => {
        if(!selectedDomain || !clientId) return;
        setIsLoading(true);
        const response = await getShopClient(selectedDomain, clientId);
        console.log("response: ", response);
        if(response.data) {
            setClient(response.data);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        getClient();
    }, [clientId]);

    if(!client?.connection?.id) return null;

    return (
        <Panel className="client-panel" isOpen={!!clientId} onClose={onClose} width={420}>
            {isLoading ? <Loader size={72} /> : <>
                <div className="head">
                    <div className="profile-image-container">
                        {client.connection.countryCode ? <div className="phone-flag-container"><img alt={client.connection.countryCode} className="phone-flag" src={`https://flagcdn.com/w40/${client.connection.countryCode.toLowerCase()}.png`} /></div> : null}
                        <Avatar width={72} height={72} color="#dbdbdb" />
                    </div>
                    <div className="client-details">
                        <div className="title">
                            {client.connection.nameOnShop || `${client?.firstName} ${client?.lastName}`}
                        </div>
                        <div className="subtitle">
                            {formatPhoneNumber(client.connection.phone)}
                        </div>
                        {subscribed ? <div className="client-detail consent"><CheckCircleFilled /><span>Subscribed</span></div> : null}
                        <div className="client-detail creation-date">Registered {getFriendlyDate(new Date(client.connection.createdAt))}</div>
                    </div>
                </div>
                <div className="orders-details">
                    <div className="title">Recorded Activity</div>
                    <div className="list">
                        <div className="list-item">
                            <label>Total Spending:</label>
                            <div>{millify(client.connection.amountSpent)} {client.connection.amountCurrency}</div>
                        </div>
                        <div className="list-item">
                            <label>Total Orders:</label>
                            <div>{millify(client.connection.ordersCount)} Orders</div>
                        </div>
                        {client.connection.lastOrderedAt ? <div className="list-item">
                            <label>Last Order:</label>
                            <div>{getFriendlyDate(new Date(client.connection.lastOrderedAt))}</div>
                        </div> : null}
                        {messagesSent ? <div className="list-item">
                            <label>All Messages Sent</label>
                            <div>{millify(messagesSent)} <span>({millify(messagesClicked)} Clicked)</span></div>
                        </div> : null}
                        {abandonedMessagesSent ? <div className="list-item">
                            <label>Recovery Messages Sent</label>
                            <div>{millify(abandonedMessagesSent)} <span>({millify(abandonedMessagesClicked)} Clicked)</span></div>
                        </div> : null}
                        {eventMessagesSent ? <div className="list-item">
                            <label>Event Messages Sent</label>
                            <div>{millify(eventMessagesSent)} <span>({millify(eventMessagesClicked)} Clicked)</span></div>
                        </div> : null}
                        {campaignMessagesSent ? <div className="list-item">
                            <label>Campaign Messages Sent</label>
                            <div>{millify(campaignMessagesSent)} <span>({millify(campaignMessagesClicked)} Clicked)</span></div>
                        </div> : null}
                    </div>

                    {client.orders?.length ? <>
                        <div className="title">Recorded Orders ({client.orders?.length})</div>
                        <div className="list">
                            {client.orders?.map((order: { id: string, price: number, currency: string, createdAt: Date }) =>
                                <div className="list-item">
                                    <label>{order.id}</label>
                                    <label>{order.price} {order.currency}</label>
                                    <div><Tooltip title={formatDateForAmPmDisplay(new Date(order.createdAt))}>{getFriendlyDate(new Date(order.createdAt))}</Tooltip></div>
                                </div>
                            )}
                        </div>
                    </> : null}

                    {client.conversations?.length ? <>
                        <div className="title">Conversation Threads ({client.conversations?.length})</div>
                        <div className="list">
                            {client.conversations?.map((conv: any) => <div className="conversation list-item" onClick={() => onConversationClick(conv)}>
                                <label>{conv.widgetSessionId ? <>Support {conv.resolvedAt ? <span>(Resolved in {millisecondsToHuman(new Date(conv.resolvedAt).getTime() - new Date(conv.createdAt).getTime())})</span> : null}</> : 'Main Conversation'}</label>
                                <div>{formatDateForAmPmDisplay(new Date(conv.updatedAt))}</div>
                            </div>)}
                        </div>
                    </> : null}
                </div>
            </>}
        </Panel>
    )
}

export default ClientPanel;